/**
 * Procesa los datos crudos de Table1 de Detalle de Interacciones
 * y genera métricas para el dashboard.
 */

/** Formatea segundos a HH:MM:SS */
export function fmtSeg(s) {
  if (!s || s === 0) return '00:00:00';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.round(s % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

export function procesarDatosDetalleLlamadas(rows) {
  if (!rows || rows.length === 0) return null;

  const n = (v) => parseFloat(v) || 0;

  // --- KPIs globales ---
  let totalInteracciones = rows.length;
  let totalAtendidas = 0;
  let totalAbandonadas = 0;
  let totalDuracion = 0;
  let totalTalkingTime = 0;
  let totalHold = 0;
  let totalACW = 0;
  let totalEnCola = 0;
  let totalTransferIn = 0;
  let totalTransferOut = 0;

  // Contadores por tipo
  const porTipoContacto = {};
  const porSentido = {};
  const porCampania = {};
  const porCausaTerminacion = {};
  const porResultadoGestion = {};
  const porHora = {};   // hora del día -> count
  const porFecha = {};  // fecha -> { total, atendidas, abandonadas, duracion, talkingTime }
  const agentesSet = new Set();

  rows.forEach((r) => {
    const atendida = n(r.Atendidas);
    const abandonada = n(r.Abandonada);
    const duracion = n(r.Duracion);
    const talkingTime = n(r.TalkingTime);
    const hold = n(r.Hold);
    const acw = n(r.ACW);
    const enCola = n(r.EnCola);

    totalAtendidas += atendida;
    totalAbandonadas += abandonada;
    totalDuracion += duracion;
    totalTalkingTime += talkingTime;
    totalHold += hold;
    totalACW += acw;
    totalEnCola += enCola;
    totalTransferIn += n(r.TransferIn);
    totalTransferOut += n(r.TransferOut);

    // Agentes únicos
    if (r.NombreAgente) agentesSet.add(r.NombreAgente);

    // Por tipo de contacto
    const tipo = r.TipoContacto || 'Sin tipo';
    porTipoContacto[tipo] = (porTipoContacto[tipo] || 0) + 1;

    // Por sentido
    const sentido = r.Sentido || 'Sin datos';
    porSentido[sentido] = (porSentido[sentido] || 0) + 1;

    // Por campaña
    const camp = r.NombreCampania || 'Sin campaña';
    if (!porCampania[camp]) porCampania[camp] = { total: 0, atendidas: 0, abandonadas: 0, duracion: 0 };
    porCampania[camp].total++;
    porCampania[camp].atendidas += atendida;
    porCampania[camp].abandonadas += abandonada;
    porCampania[camp].duracion += duracion;

    // Por causa de terminación
    const causa = r.CausaTerminacion || 'Sin datos';
    porCausaTerminacion[causa] = (porCausaTerminacion[causa] || 0) + 1;

    // Por resultado de gestión
    const rg = r.ResultadoGestion || 'Sin datos';
    porResultadoGestion[rg] = (porResultadoGestion[rg] || 0) + 1;

    // Por hora del día
    if (r.Inicio) {
      const d = new Date(r.Inicio);
      if (!isNaN(d)) {
        const hora = d.getHours();
        porHora[hora] = (porHora[hora] || 0) + 1;

        const fechaKey = d.toISOString().split('T')[0];
        if (!porFecha[fechaKey]) {
          porFecha[fechaKey] = { total: 0, atendidas: 0, abandonadas: 0, duracion: 0, talkingTime: 0 };
        }
        porFecha[fechaKey].total++;
        porFecha[fechaKey].atendidas += atendida;
        porFecha[fechaKey].abandonadas += abandonada;
        porFecha[fechaKey].duracion += duracion;
        porFecha[fechaKey].talkingTime += talkingTime;
      }
    }
  });

  // --- Promedios ---
  const promDuracion = totalInteracciones > 0 ? totalDuracion / totalInteracciones : 0;
  const promTalkingTime = totalAtendidas > 0 ? totalTalkingTime / totalAtendidas : 0;
  const promACW = totalAtendidas > 0 ? totalACW / totalAtendidas : 0;
  const promEnCola = totalInteracciones > 0 ? totalEnCola / totalInteracciones : 0;
  const pctAtendidas = totalInteracciones > 0 ? (totalAtendidas / totalInteracciones) * 100 : 0;

  // --- Datos para gráficos ---

  // Gráfico por hora del día (0-23)
  const horasLabels = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`);
  const horasData = Array.from({ length: 24 }, (_, i) => porHora[i] || 0);

  // Gráfico por fecha
  const fechasOrdenadas = Object.keys(porFecha).sort();
  const chartPorFecha = {
    labels: fechasOrdenadas.map((f) => {
      const [, m, d] = f.split('-');
      return `${d}/${m}`;
    }),
    total: fechasOrdenadas.map((f) => porFecha[f].total),
    atendidas: fechasOrdenadas.map((f) => porFecha[f].atendidas),
    abandonadas: fechasOrdenadas.map((f) => porFecha[f].abandonadas),
    promDuracion: fechasOrdenadas.map((f) =>
      porFecha[f].total > 0 ? Math.round(porFecha[f].duracion / porFecha[f].total) : 0
    ),
  };

  // Top campañas (ordenadas por total descendente)
  const campaniasSorted = Object.entries(porCampania)
    .sort((a, b) => b[1].total - a[1].total);
  const chartCampanias = {
    labels: campaniasSorted.map(([k]) => k.length > 25 ? k.slice(0, 22) + '...' : k),
    fullLabels: campaniasSorted.map(([k]) => k),
    total: campaniasSorted.map(([, v]) => v.total),
    atendidas: campaniasSorted.map(([, v]) => v.atendidas),
    abandonadas: campaniasSorted.map(([, v]) => v.abandonadas),
  };

  // Tipo de contacto (para doughnut)
  const tipoContactoEntries = Object.entries(porTipoContacto).sort((a, b) => b[1] - a[1]);

  // Sentido (para doughnut)
  const sentidoEntries = Object.entries(porSentido).sort((a, b) => b[1] - a[1]);

  // Causa terminación (para horizontal bar - top 10)
  const causaEntries = Object.entries(porCausaTerminacion)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);

  // Resultado de gestión (top 15 para bar)
  const resultadoEntries = Object.entries(porResultadoGestion)
    .filter(([k]) => k !== 'No Disp.')
    .sort((a, b) => b[1] - a[1])
    .slice(0, 15);

  return {
    kpis: {
      totalInteracciones,
      totalAtendidas,
      totalAbandonadas,
      pctAtendidas,
      totalDuracion,
      promDuracion,
      promTalkingTime,
      promACW,
      promEnCola,
      totalHold,
      totalTransferIn,
      totalTransferOut,
      agentesUnicos: agentesSet.size,
    },
    chartPorHora: { labels: horasLabels, data: horasData },
    chartPorFecha,
    chartCampanias,
    tipoContacto: tipoContactoEntries,
    sentido: sentidoEntries,
    causaTerminacion: causaEntries,
    resultadoGestion: resultadoEntries,
    rows,
  };
}
